/**
 * GuidePopover — reusable inline guide system.
 *
 * Usage:
 *   <GuidePopover slides={[
 *     { title: "What is this?", body: "Explanation here." },
 *     { title: "How it works", body: "Step-by-step." },
 *   ]} />
 *
 * Renders a small (?) icon. Click opens a dialog with swipeable slide cards.
 * Designed to be dropped next to any label/title across the entire app.
 */

import { useState, useCallback } from "react";
import { HelpCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface GuideSlide {
  title: string;
  body: string;
  /** Optional icon component rendered above the title. */
  icon?: React.ComponentType<{ className?: string }>;
  /** Optional illustration (SVG/JSX) rendered above the title area. */
  illustration?: React.ReactNode;
}

interface GuidePopoverProps {
  slides: GuideSlide[];
  /** Override the trigger icon size. Default: "h-4 w-4". */
  iconClassName?: string;
}

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 120 : -120,
    opacity: 0,
  }),
  center: { x: 0, opacity: 1 },
  exit: (direction: number) => ({
    x: direction > 0 ? -120 : 120,
    opacity: 0,
  }),
};

export function GuidePopover({ slides, iconClassName }: GuidePopoverProps) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [direction, setDirection] = useState(0);

  const total = slides.length;

  const go = useCallback(
    (dir: number) => {
      setDirection(dir);
      setPage((p) => Math.max(0, Math.min(total - 1, p + dir)));
    },
    [total],
  );

  const handleOpen = (v: boolean) => {
    setOpen(v);
    if (v) {
      setPage(0);
      setDirection(0);
    }
  };

  const slide = slides[page];

  return (
    <>
      <button
        type="button"
        onClick={() => handleOpen(true)}
        className="inline-flex items-center justify-center rounded-full text-muted-foreground/50 hover:text-muted-foreground transition-colors"
        aria-label="Open guide"
      >
        <HelpCircle className={cn("h-4 w-4", iconClassName)} />
      </button>

      <Dialog open={open} onOpenChange={handleOpen}>
        <DialogContent className="sm:max-w-sm md:max-w-sm md:p-0" hideCloseButton>
          <DialogTitle className="sr-only">Guide</DialogTitle>

          <div className="p-6 pb-4 min-h-[240px] flex flex-col">
            {/* Slide content with animation */}
            <div className="flex-1 relative overflow-hidden">
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={page}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                  className="space-y-3"
                >
                  {slide.illustration && (
                    <div className="flex items-center justify-center mb-1">
                      {slide.illustration}
                    </div>
                  )}
                  {slide.icon && !slide.illustration && (
                    <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10">
                      <slide.icon className="h-5 w-5 text-primary" />
                    </div>
                  )}
                  <h3 className="text-base font-semibold">{slide.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {slide.body}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Footer: dots + nav */}
            <div className="flex items-center justify-between pt-5">
              {/* Page dots */}
              <div className="flex items-center gap-1.5">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => {
                      setDirection(i > page ? 1 : -1);
                      setPage(i);
                    }}
                    className={cn(
                      "h-1.5 rounded-full transition-all duration-200",
                      i === page
                        ? "w-4 bg-primary"
                        : "w-1.5 bg-muted-foreground/25 hover:bg-muted-foreground/40",
                    )}
                    aria-label={`Go to slide ${i + 1}`}
                  />
                ))}
              </div>

              {/* Nav buttons */}
              <div className="flex items-center gap-1.5">
                {page > 0 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => go(-1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                )}
                {page < total - 1 ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => go(1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    variant="default"
                    size="sm"
                    className="h-8 px-4 text-xs"
                    onClick={() => handleOpen(false)}
                  >
                    เข้าใจแล้ว
                  </Button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
